import React, { useCallback, useState, useEffect } from 'react';
import axios from 'src/api/axios';
import { Link, useNavigate } from 'react-router-dom';
import { NotificationManager } from 'react-notifications';
import { useDispatch } from 'react-redux';
import { showModal, hideModal } from 'src/redux/actions/modalActions';
import FacebookLoginButton from 'src/components/form/social-buttons/FacebookLoginButton';
import GoogleLoginButton from 'src/components/form/social-buttons/GoogleLoginButton';

function RegisterForm(props) {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const recaptchaEnabled = process.env.REACT_APP_RECAPTCHA_ENABLED === 'true';

  // Load reCAPTCHA script
  const loadRecaptcha = useCallback(() => {
    return new Promise((resolve, reject) => {
      if (window.grecaptcha && window.grecaptcha.ready) {
        window.grecaptcha.ready(resolve);
        return;
      }
      
      const existingScript = document.querySelector('script[src*="recaptcha"]');
      if (existingScript) {
        existingScript.addEventListener('load', () => {
          window.grecaptcha.ready(resolve);
        });
        return;
      }
      
      const script = document.createElement('script');
      script.src = `https://www.google.com/recaptcha/api.js?render=${process.env.REACT_APP_RECAPTCHA_SITE_KEY}`;
      script.async = true;
      script.onload = () => {
        window.grecaptcha.ready(resolve);
      };
      script.onerror = reject;
      document.head.appendChild(script);
    });
  }, []);

  useEffect(() => {
    if (recaptchaEnabled) {
      loadRecaptcha().catch((error) => {
        console.error('Error loading reCAPTCHA:', error);
      });
    }
  }, [loadRecaptcha, recaptchaEnabled]);

  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    password: '',
    password_confirmation: '',
    terms_and_conditions: false
  });

  const [errors, setErrors] = useState({});

  const handleSubmit = async (event) => {
    event.preventDefault();
    try {
      let requestData = {
        first_name: formData.first_name,
        last_name: formData.last_name,
        email: formData.email,
        password: formData.password,
        password_confirmation: formData.password_confirmation,
        terms_and_conditions: formData.terms_and_conditions,
      };

      // Execute reCAPTCHA only if enabled
      if (recaptchaEnabled) {
        if (!window.grecaptcha) {
          await loadRecaptcha();
        }
        
        const recaptchaToken = await window.grecaptcha.execute(process.env.REACT_APP_RECAPTCHA_SITE_KEY, {
          action: 'register'
        });
        requestData.recaptcha_token = recaptchaToken;
      }

      const response = await axios.post('api/register', requestData, {
        disableNotification: true // Disable axios interceptor notifications, we handle errors manually
      });
      
      dispatch({ type: 'LOGIN_SUCCESS', payload: response.data });
      dispatch(hideModal());
      NotificationManager.success('Registration successful');
      
      // Proveri da li postoji sačuvana lokacija za redirect nakon prijave
      const redirectPath = localStorage.getItem('redirectAfterLogin');
      if (redirectPath) {
        localStorage.removeItem('redirectAfterLogin');
        navigate(redirectPath);
      } else {
        navigate('/');
      }
    } catch (error) {
      console.error('Registration error:', error);
      console.error('Error response:', error.response?.data);
      
      // Clear previous errors
      setErrors({});
      
      if (!error.response) {
        NotificationManager.error('No server response');
      } else if (error.response?.status === 400 || error.response?.status === 401 || error.response?.status === 422) {
        // Handle validation errors from backend
        const errorData = error.response.data;
        
        // Laravel validation errors format: { error: { field: ["message1", "message2"] } }
        const validationErrors = errorData.error || errorData.errors || {};
        
        if (validationErrors && typeof validationErrors === 'object' && !Array.isArray(validationErrors)) {
          // Format errors for display: { field: "first message" }
          const formattedErrors = {};
          
          for (const [key, value] of Object.entries(validationErrors)) {
            if (Array.isArray(value) && value.length > 0) {
              // Take the first error message for each field
              formattedErrors[key] = typeof value[0] === 'string' ? value[0] : String(value[0]);
            } else if (typeof value === 'string') {
              formattedErrors[key] = value;
            }
          }
          
          setErrors(formattedErrors);
          
          // Also show a general notification
          NotificationManager.error('Please correct the errors in the form');
        } else if (errorData.message) {
          NotificationManager.error(String(errorData.message));
        } else {
          NotificationManager.error('Validation failed. Please check your input.');
        }
      } else {
        NotificationManager.error(error.message || 'An error occurred');
        if (error.response?.data?.message) {
          NotificationManager.error(error.response.data.message);
        }
      }
    }
  };

  const handleChange = (event) => {
    const { name, value, type, checked } = event.target;
    setFormData((prevFormData) => ({
      ...prevFormData,
      [name]: type === 'checkbox' ? checked : value
    }));
    
    // Clear error for this field when user starts typing
    if (errors[name]) {
      setErrors((prevErrors) => {
        const newErrors = { ...prevErrors };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  return (
    <>
      <div className="w-full mx-auto bg-white sm:max-w-md md:max-w-[950px]">
        <div className="mb-10">
          <h2 className="text-[35px] leading-[45px] font-bold text-center">
            Sign up
          </h2>
          <p className="text-base mt-4 mb-6 text-center">
            Already have an account?{' '}
            <span
              onClick={() => dispatch(showModal('LOGIN_MODAL'))}
              className="font-bold text-[#832EE6] cursor-pointer"
            >
              Login
            </span>
          </p>
          <form onSubmit={handleSubmit} className="w-full">
            <div className="flex w-full mb-4 items-center">
              <label htmlFor="first_name" className="w-fit min-w-[120px] text-sm font-semibold text-gray-700 hidden sm:block">
                Full name
              </label>
              <div className="w-full flex gap-4">
                <div className="flex-1">
                  <input
                    type="text"
                    id="first_name"
                    name="first_name"
                    value={formData.first_name}
                    onChange={handleChange}
                    className={`bg-gray-50 border sm:text-sm rounded focus:ring-[#832EE6] focus:border-[#832EE6] placeholder-[#0D0D0D] block w-full p-4 ${errors.first_name ? 'border-red-500' : 'border-gray-300'}`}
                    placeholder="First name"
                    required=""
                  />
                  {errors.first_name && (
                    <p className="text-red-500 text-xs mt-1">{errors.first_name}</p>
                  )}
                </div>
                <div className="flex-1">
                  <input
                    type="text"
                    id="last_name"
                    name="last_name"
                    value={formData.last_name}
                    onChange={handleChange}
                    placeholder="Last name"
                    className={`bg-gray-50 border sm:text-sm rounded focus:ring-[#832EE6] focus:border-[#832EE6] placeholder-[#0D0D0D] block w-full p-4 ${errors.last_name ? 'border-red-500' : 'border-gray-300'}`}
                    required=""
                  />
                  {errors.last_name && (
                    <p className="text-red-500 text-xs mt-1">{errors.last_name}</p>
                  )}
                </div>
              </div>
            </div>

            <div className="flex w-full mb-4 items-center">
              <label htmlFor="email" className="w-fit min-w-[120px] text-sm font-semibold text-gray-700 hidden sm:block">
                Email
              </label>
              <div className="w-full">
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="Email"
                  className={`bg-gray-50 border sm:text-sm rounded focus:ring-[#832EE6] focus:border-[#832EE6] placeholder-[#0D0D0D] block w-full p-4 ${errors.email ? 'border-red-500' : 'border-gray-300'}`}
                  required=""
                />
                {errors.email && (
                  <p className="text-red-500 text-xs mt-1">{errors.email}</p>
                )}
              </div>
            </div>

            <div className="flex w-full mb-4 items-center">
              <label htmlFor="password" className="w-fit min-w-[120px] text-sm font-semibold text-gray-700 hidden sm:block">
                Password
              </label>
              <div className="w-full flex gap-4">
                <div className="flex-1">
                  <input
                    type="password"
                    id="password"
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    placeholder="Password"
                    className={`bg-gray-50 border sm:text-sm rounded focus:ring-[#832EE6] focus:border-[#832EE6] placeholder-[#0D0D0D] block w-full p-4 ${errors.password ? 'border-red-500' : 'border-gray-300'}`}
                    required=""
                  />
                  {errors.password && (
                    <p className="text-red-500 text-xs mt-1">{errors.password}</p>
                  )}
                </div>
                <div className="flex-1">
                  <input
                    type="password"
                    id="password_confirmation"
                    name="password_confirmation"
                    value={formData.password_confirmation}
                    onChange={handleChange}
                    placeholder="Confirm password"
                    className={`bg-gray-50 border sm:text-sm rounded focus:ring-[#832EE6] focus:border-[#832EE6] placeholder-[#0D0D0D] block w-full p-4 ${errors.password_confirmation ? 'border-red-500' : 'border-gray-300'}`}
                    required=""
                  />
                  {errors.password_confirmation && (
                    <p className="text-red-500 text-xs mt-1">{errors.password_confirmation}</p>
                  )}
                </div>
              </div>
            </div>

            <div className="w-full flex items-center mt-4">
              <div className="flex">
                <input
                  id="terms_and_conditions"
                  name="terms_and_conditions"
                  aria-describedby="terms_and_conditions"
                  type="checkbox"
                  checked={formData.terms_and_conditions}
                  className="w-4 h-4 accent-purple-600 text-purple-600 bg-gray-100 border-gray-300 rounded focus:ring-purple-500 dark:focus:ring-purple-600 dark:ring-offset-gray-800 dark:bg-gray-700 dark:border-gray-600"
                  required={true}
                  onChange={handleChange}
                />
                <div className="ml-3 text-sm">
                  <label htmlFor="terms_and_conditions" className="font-light text-gray-500">
                    I accept the{' '}
                    <Link className="font-medium text-[#832EE6]" to="/terms-and-conditions" onClick={() => dispatch(hideModal())}>
                      Terms and Conditions
                    </Link>
                  </label>
                </div>
              </div>
            </div>
            <div className="flex mt-10 mb-12 w-full">
              <button
                type="submit"
                className="bg-[#832EE6] hover:bg-purple-800 text-white px-[45px] mx-auto py-[15px] text-[18px] leading-6 font-bold inline-block rounded-[27px]"
              >
                Sign up
              </button>
            </div>
          </form>
          {(process.env.REACT_APP_FB_APP_ID || process.env.REACT_APP_GG_APP_ID) && (
            <div className='max-w-md mx-auto'>
              <p className='text-center font-bold text-base mb-8'>Or sign up with your social media account</p>
              <div className='flex gap-4 justify-between'>
                <FacebookLoginButton />
                <GoogleLoginButton />
              </div> 
            </div>
          )}
        </div>
      </div>
    </>
  );
}

export default RegisterForm;